import * as React from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { UniversalImporterContractsPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NuvoImporter from 'modules/universal_importer/components/NuvoImporter'

type LocationStateType = {
  employeesIds?: string[]
  selectedLegalEntity?: string
  contractMode?: 'all' | 'current'
} | null

const UniversalImporterContractsPage = () => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const location = useLocation()
  const locationState: LocationStateType = location.state
  const selectedIds = new Set(locationState?.employeesIds ?? [])
  const selectedLegalEntityId = locationState?.selectedLegalEntity
  const only_reference_contracts = locationState?.contractMode !== 'all'

  const nuvo_import_type = 'contract_version_importer'
  const { data, loading, error } = useQuery(UniversalImporterContractsPageDocument, {
    variables: { companyId: company.id, legalEntityId: selectedLegalEntityId ?? '' },
    fetchPolicy: 'network-only',
    skip: !selectedLegalEntityId,
  })

  const canImportResources = data?.universalImporter._policies.importResources.can
  const canEditContracts = data?.contracts._policies.contractEditContractVersion.can

  const authorized = canImportResources && canEditContracts

  const legalEntityCountry = data?.companies.legalEntitiesConnection.nodes[0]?.country
  const legalEntitiesIds = React.useMemo(
    () => (selectedLegalEntityId ? [BigInt(selectedLegalEntityId)] : []),
    [selectedLegalEntityId]
  )
  const metadata = {
    legal_entity_ids: legalEntitiesIds,
    employee_ids: Array.from(selectedIds).map(BigInt),
    legal_entity_countries: legalEntityCountry ? [legalEntityCountry] : [],
    only_reference_contracts: [only_reference_contracts],
  }

  if (loading) {
    return <Loading />
  }

  return (
    <ResponsivePage.Body
      authorized={authorized}
      loading={loading}
      error={!selectedLegalEntityId || error}
      showHeader={false}
    >
      <NuvoImporter
        importType={nuvo_import_type}
        locale={locale}
        companyId={company.id}
        metadata={metadata}
        isEdition // Loads data from DB and shows hidden fields
        forceCreateAction // Sends isEdition=false to backend so it creates instead of updates
      />
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default UniversalImporterContractsPage
